/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVertical, ListChecks, Plus, Trash2, X } from 'lucide-react';
import {
  SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH,
  SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH,
  SERVICE_REQUEST_MAX_CHECKLISTS,
  SERVICE_REQUEST_CHECKLISTS_UNLIMITED,
  SERVICE_REQUEST_CHECKLIST_SECTION_LABEL,
} from '../../constants/serviceRequests';
import ChecklistIconTooltip from './ChecklistIconTooltip';
import ChecklistItemNumber from './ChecklistItemNumber';

// The server's MAX_ITEMS_PER_CHECKLIST.
const MAX_ITEMS = 200;

let nextKey = 0;
const newKey = () => `draft-${(nextKey += 1)}`;

export const createDraftChecklist = (title = '') => ({ key: newKey(), title, items: [] });

/**
 * Turns draft checklists into the create payload: trims text, drops blank items
 * and fully empty checklists. Returns null when a checklist with items has no title.
 */
export const toChecklistPayload = (drafts) => {
  const result = [];
  for (const draft of drafts) {
    const title = draft.title.trim();
    const items = draft.items.map((i) => i.title.trim()).filter(Boolean);
    if (!title && !items.length) continue;
    if (!title) return null;
    result.push({ title, items });
  }
  return result;
};

// Enter inside the outer service request form must not submit it.
const onEnter = (handler) => (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    handler();
  }
};

const DraftItemRow = ({ item, number, onChange, onRemove }) => {
  const dragControls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={dragControls}
      className="relative bg-base-100 rounded-lg"
      whileDrag={{ scale: 1.01, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10 }}
    >
      <div className="flex items-center gap-2 p-1.5 rounded-lg border border-base-content/5">
        <ChecklistIconTooltip tip="Drag to reorder">
          <button
            type="button"
            aria-label="Drag to reorder"
            onPointerDown={(e) => dragControls.start(e)}
            style={{ touchAction: 'none' }}
            className="p-0.5 text-base-content/30 hover:text-base-content/70 cursor-grab active:cursor-grabbing"
          >
            <GripVertical size={16} />
          </button>
        </ChecklistIconTooltip>
        <ChecklistItemNumber number={number} className="shrink-0" />
        <input
          type="text"
          value={item.title}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onEnter(() => {})}
          maxLength={SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH}
          aria-label="Checklist item title"
          className="flex-1 min-w-0 bg-transparent text-sm text-base-content outline-none"
        />
        <ChecklistIconTooltip tip="Remove" variant="error" position="left">
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove item"
            className="p-1 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
          >
            <X size={13} />
          </button>
        </ChecklistIconTooltip>
      </div>
    </Reorder.Item>
  );
};

DraftItemRow.propTypes = {
  item: PropTypes.shape({ key: PropTypes.string.isRequired, title: PropTypes.string.isRequired }).isRequired,
  number: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const DraftChecklist = ({ draft, error, autoFocusTitle, canAddItems, onChange, onRemove }) => {
  const [itemDraft, setItemDraft] = useState('');
  const itemInputRef = useRef(null);

  const setItems = (items) => onChange({ ...draft, items });

  // Focus returns to the item input (also when added via the button, which
  // disables itself once the input clears), so the next item can be typed at once.
  const addItem = () => {
    const title = itemDraft.trim();
    if (!title || draft.items.length >= MAX_ITEMS) return;
    setItems([...draft.items, { key: newKey(), title }]);
    setItemDraft('');
    itemInputRef.current?.focus();
  };

  return (
    <div className={`rounded-xl border bg-base-200/20 p-3 sm:p-4 ${error ? 'border-error' : 'border-base-content/10'}`}>
      <div className="flex items-center gap-2 mb-3">
        <input
          type="text"
          value={draft.title}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
          onKeyDown={onEnter(() => itemInputRef.current?.focus())}
          autoFocus={autoFocusTitle}
          maxLength={SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH}
          placeholder="Checklist title"
          aria-label="Checklist title"
          className="input input-sm input-bordered flex-1 min-w-0 rounded-lg text-sm font-bold focus:outline-none focus:border-primary"
        />
        <span className="text-xs font-bold text-base-content/50 whitespace-nowrap">
          {draft.items.length} {draft.items.length === 1 ? 'item' : 'items'}
        </span>
        <ChecklistIconTooltip tip="Remove checklist" variant="error" position="left">
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove checklist"
            className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
          >
            <Trash2 size={13} />
          </button>
        </ChecklistIconTooltip>
      </div>
      {error && <p className="text-error text-xs font-medium -mt-2 mb-2">{error}</p>}

      {draft.items.length > 0 && (
        <Reorder.Group as="div" axis="y" values={draft.items} onReorder={setItems} className="space-y-1.5 mb-3">
          {draft.items.map((item, index) => (
            <DraftItemRow
              key={item.key}
              item={item}
              number={index + 1}
              onChange={(title) => setItems(draft.items.map((i) => (i.key === item.key ? { ...i, title } : i)))}
              onRemove={() => setItems(draft.items.filter((i) => i.key !== item.key))}
            />
          ))}
        </Reorder.Group>
      )}

      {canAddItems && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={itemDraft}
            onChange={(e) => setItemDraft(e.target.value)}
            ref={itemInputRef}
            onKeyDown={onEnter(addItem)}
            maxLength={SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH}
            placeholder="Add an item and press Enter..."
            aria-label="New checklist item"
            className="input input-sm input-bordered flex-1 min-w-0 rounded-lg text-sm bg-base-100 focus:outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={addItem}
            disabled={!itemDraft.trim() || draft.items.length >= MAX_ITEMS}
            className="btn btn-sm btn-primary rounded-lg gap-1 font-bold"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      )}
    </div>
  );
};

DraftChecklist.propTypes = {
  draft: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
  error: PropTypes.string,
  autoFocusTitle: PropTypes.bool,
  canAddItems: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

/**
 * Checklists drafted on the New Service Request page. Nothing is saved until the
 * request is created; the parent sends `toChecklistPayload(value)` with the create call.
 * Comments and ticking items are available on the detail page once it exists.
 * `canAddItems` mirrors the checklists.item_add permission.
 */
const ChecklistDraftBuilder = ({ value, onChange, error, canAddItems = true }) => {
  // Key of the checklist just added with the button, whose title input gets focus.
  const [addedKey, setAddedKey] = useState(null);
  const update = (key, next) => onChange(value.map((d) => (d.key === key ? next : d)));

  const addChecklist = () => {
    const draft = createDraftChecklist();
    setAddedKey(draft.key);
    onChange([...value, draft]);
  };

  return (
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
        <ListChecks size={18} className="text-primary" />
        <h2 className="text-base font-black text-base-content uppercase tracking-wider">
          {SERVICE_REQUEST_CHECKLIST_SECTION_LABEL}
        </h2>
        {/* Like the detail page tab: a count only when more than one is allowed. */}
        {SERVICE_REQUEST_MAX_CHECKLISTS > 1 && (
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
            {value.length}
          </span>
        )}
        {value.length < SERVICE_REQUEST_MAX_CHECKLISTS && (
          <button
            type="button"
            onClick={addChecklist}
            className="ml-auto btn btn-sm btn-ghost rounded-xl gap-1 font-bold text-primary"
          >
            <Plus size={14} />
            Add Checklist
          </button>
        )}
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-base-content/40">
          {SERVICE_REQUEST_MAX_CHECKLISTS === 1
            ? 'Optional. Add a checklist now, or later from the detail page.'
            : SERVICE_REQUEST_CHECKLISTS_UNLIMITED
              ? 'Optional. Add checklists now, or later from the detail page.'
              : `Optional. Add up to ${SERVICE_REQUEST_MAX_CHECKLISTS} checklists now, or later from the detail page.`}
        </p>
      ) : (
        <div className="space-y-3">
          {value.map((draft) => (
            <DraftChecklist
              key={draft.key}
              draft={draft}
              autoFocusTitle={draft.key === addedKey}
              canAddItems={canAddItems}
              error={error && !draft.title.trim() && draft.items.some((i) => i.title.trim()) ? error : undefined}
              onChange={(next) => update(draft.key, next)}
              onRemove={() => onChange(value.filter((d) => d.key !== draft.key))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

ChecklistDraftBuilder.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  canAddItems: PropTypes.bool,
};

export default ChecklistDraftBuilder;
